import { type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui/ToastProvider';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import { Sentry } from './lib/sentry';
import ErrorFallback from './components/ErrorFallback';
import { supabase } from './lib/supabase';
import Layout from './components/Layout';
import { 
  Clock, 
  Calendar, 
  Users, 
  LogOut, 
  ShieldAlert,
  MapPin,
  FileText,
  Package // Ajout icône Package
} from 'lucide-react';

// --- PAGES EMPLOYÉS ---
import Login from './pages/Login';
import Timesheet from './pages/Timesheet';
import TeamManager from './pages/TeamManager';
import TeamMemberProfile from './pages/TeamMemberProfile';
import LeaveRequest from './pages/LeaveRequest';
import Planning from './pages/Planning'; 
import MyDocuments from './pages/MyDocuments'; 
import DocEmployee from './pages/DocEmployee'; 

// --- PAGES ADMINISTRATION ---
import AdminDashboard from './pages/AdminDashboard';
import AdminValidate from './pages/AdminValidate';
import AdminLeaves from './pages/AdminLeaves';
import AdminEmployees from './pages/AdminEmployees';
import AdminProjects from './pages/AdminProjects';
import AdminReporting from './pages/AdminReporting';
import AdminPlanning from './pages/AdminPlanning';
import AdminScheduler from './pages/AdminScheduler';
import AdminDocuments from './pages/AdminDocuments';
import MaterialRequest from './pages/MaterialRequest';
import AdminMaterials from './pages/AdminMaterials';
import AdminAssistant from './pages/AdminAssistant';
import AdminTraining from './pages/AdminTraining';
import AdminDivers from './pages/AdminDivers';
import AdminPurchases from './pages/AdminPurchases';
import AdminInvoicing from './pages/AdminInvoicing';
import PriceComparator from './pages/PriceComparator';
import PlanningView from './pages/PlanningView';
import DocAdmin from './pages/DocAdmin';
import SecretariatDashboard from './pages/SecretariatDashboard';
import SecretariatValidate from './pages/SecretariatValidate';
import SecretariatPayroll from './pages/SecretariatPayroll';
import SecretariatDocuments from './pages/SecretariatDocuments';
import SecretariatLeaves from './pages/SecretariatLeaves';
import SecretariatHours from './pages/SecretariatHours';

// --- PAGE BUREAU D'ÉTUDE ---
import BEDashboard from './pages/BEDashboard';

import { MANAGEMENT_ROLES as ROLE_MANAGEMENT } from './lib/roles';


// --- DÉFINITION DES ROLES TECHNIQUES (Qui peuvent avoir un intérimaire) ---
const TECHNICAL_ROLES = ['admin', 'chef_chantier', 'chef_equipe', 'electricien', 'bureau_etude', 'apprenti_be'];

// --- DÉFINITION DES ROLES DE GESTION (Direction + BE confirmé) ---
// Ces rôles accèdent aux outils de gestion (CRUD, Rentabilité, Matériel...)
// Défini dans lib/roles.ts : ce groupe est aussi lu par la fonction de fitness
// qui vérifie l'accord entre les droits de l'application et ceux de la base.
// Le dupliquer ici rouvrirait la divergence corrigée le 10/08/2026.
const MANAGEMENT_ROLES = ROLE_MANAGEMENT;


// --- COMPOSANT GARDIEN (SÉCURITÉ) ---
// allowedRoles est readonly : les groupes de rôles viennent de lib/roles.ts et
// sont immuables par construction. Le composant se contente de les lire.
function ProtectedRoute({ children, allowedRoles }: { children: ReactNode, allowedRoles?: readonly string[] }) {
  const { user, loading } = useAuth();

  // 0. Afficher un loader pendant la vérification de session
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        flexDirection: 'column'
      }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Chargement...
        </div>
        {/* Bouton de déconnexion d'urgence (visible après 3 secondes) */}
        <button
          onClick={async () => {
            console.log('[App] Déconnexion d\'urgence...');
            await supabase.auth.signOut();
            window.location.reload();
          }}
          style={{
            marginTop: '40px',
            padding: '8px 16px',
            background: 'transparent',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.8rem',
            opacity: 0,
            animation: 'fadeIn 0.3s ease-out 3s forwards'
          }}
        >
          Problème de connexion ? Cliquez ici pour réinitialiser
        </button>
        <style>{`@keyframes fadeIn { to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // 1. Si pas connecté -> Redirection Login
  if (!user) return <Navigate to="/login" replace />;
  
  // 2. Si le rôle n'est pas autorisé -> Message d'erreur
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <Layout>
        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <ShieldAlert size={64} />
          </div>
          <h3>Accès Refusé</h3>
          <p style={{ color: '#64748b' }}>
            Votre profil <strong>{user.role}</strong> n'a pas les droits requis pour accéder à cette page.
          </p>
          <button 
            onClick={() => window.location.href = '/'} 
            style={{ 
              marginTop: '20px', 
              background: 'transparent', 
              border: '1px solid #ef4444', 
              color: '#ef4444', 
              padding: '10px 20px', 
              borderRadius: '8px', 
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Retour au Tableau de Bord
          </button>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
}

// --- ROUTAGE ADMIN MATERIALS (Filtre selon rôle) ---
function AdminMaterialsRouter() {
  const { user } = useAuth();
  // Admin (JLO) voit les demandes direction par défaut, BE (GP) voit les demandes be
  const defaultTarget = user?.role === 'bureau_etude' ? 'be' : 'direction';
  return <AdminMaterials defaultTarget={defaultTarget} />;
}

// --- DASHBOARD (ROUTAGE INTERNE SELON RÔLE) ---
function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // 1. SCÉNARIO ADMIN : Affiche le Dashboard de Pilotage
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  // 2. SCÉNARIO BUREAU D'ÉTUDE (NOUVEAU DASHBOARD DÉDIÉ)
  if (user?.role === 'bureau_etude') {
    return <BEDashboard />;
  }

  // 3. SCÉNARIO SECRÉTARIAT
  if (user?.role === 'secretariat') {
    return <SecretariatDashboard />;
  }

  // 4. SCÉNARIO EMPLOYÉS : Affiche le Menu Mobile
  const roleConfig: Record<string, { color: string, bg: string, label: string }> = {
    chef_chantier: { color: '#f59e0b', bg: '#fffbeb', label: 'Chef de Chantier' },
    chef_equipe: { color: '#0d9488', bg: '#f0fdfa', label: 'Chef d\'Équipe' },
    electricien: { color: '#10b981', bg: '#ecfdf5', label: 'Électricien Installateur' },
    // bureau_etude a son propre dashboard maintenant
    apprenti_be: { color: '#0ea5e9', bg: '#e0f2fe', label: 'Apprenti BE' }
  };
  
  const currentConfig = roleConfig[user?.role as string] || { color: '#64748b', bg: '#f1f5f9', label: user?.role || 'Employé' };

  // Vérifie si l'utilisateur a un rôle technique autorisé à gérer une équipe
  const canManageTeam = TECHNICAL_ROLES.includes(user?.role || '');

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      
      {/* En-tête Profil */}
      <div 
        className="stat-card" 
        style={{ 
          borderLeft: `6px solid ${currentConfig.color}`, 
          marginBottom: '24px',
          background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0'
        }}
      >
        <h1 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', color: '#1e293b' }}>
          Bonjour, {user?.full_name?.split(' ').slice(1).join(' ') || user?.full_name} <span style={{ fontWeight: '400', color: '#64748b' }}>— bienvenue sur votre espace</span>
        </h1>
        <span style={{ 
          background: currentConfig.bg, 
          color: currentConfig.color, 
          padding: '6px 12px', 
          borderRadius: '20px', 
          fontSize: '0.85rem', 
          textTransform: 'uppercase', 
          fontWeight: '700',
          letterSpacing: '0.05em',
          display: 'inline-block'
        }}>
          {currentConfig.label}
        </span>
      </div>
      
      <h2 style={{ fontSize: '1.2rem', marginBottom: '16px', color: '#475569' }}>Mon Espace</h2>

      <div style={{ display: 'grid', gap: '16px' }}>
        
        {/* BOUTON 1 : MON PLANNING */}
        <button 
          onClick={() => navigate('/planning')}
          style={{ 
            padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s'
          }}
        >
          <div style={{ background: '#f3e8ff', padding: '12px', borderRadius: '12px', color: '#7e22ce' }}>
            <MapPin size={28} />
          </div>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Mon Planning</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Où suis-je attendu ?</div>
          </div>
        </button>

        {/* BOUTON 2 : SAISIE HEURES */}
        <button 
          onClick={() => navigate('/timesheet')}
          style={{ 
            padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s'
          }}
        >
          <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '12px', color: '#2563eb' }}>
            <Clock size={28} />
          </div>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Saisir mes Heures</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Relevé hebdomadaire</div>
          </div>
        </button>

        {/* BOUTON 3 : CONGÉS */}
        <button 
          onClick={() => navigate('/leaves')}
          style={{ 
            padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s'
          }}
        >
          <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '12px', color: '#10b981' }}>
            <Calendar size={28} />
          </div>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Mes Absences</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Congés, Maladie</div>
          </div>
        </button>

        {/* BOUTON 4 : DOCUMENTS */}
        <button 
          onClick={() => navigate('/documents')}
          style={{ 
            padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s'
          }}
        >
          <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '12px', color: '#d97706' }}>
            <FileText size={28} />
          </div>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Mes Documents</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Feuilles signées & validées</div>
          </div>
        </button>

        {/* BOUTON 5 : MATÉRIEL */}
        <button 
          onClick={() => navigate('/materials')}
          style={{ 
            padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s'
          }}
        >
          <div style={{ background: '#ffedd5', padding: '12px', borderRadius: '12px', color: '#c2410c' }}>
            <Package size={28} />
          </div>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Matériel</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Demander des fournitures</div>
          </div>
        </button>

        {/* BOUTON 6 : ÉQUIPE (OUVERTE À TOUS LES ROLES TECHNIQUES) */}
        {canManageTeam && (
          <button 
            onClick={() => navigate('/team')}
            style={{ 
              padding: '24px', background: 'white', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', 
              display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textAlign: 'left', transition: 'transform 0.2s', marginTop: '8px'
            }}
          >
            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', color: '#475569', border:'1px solid #e2e8f0' }}>
              <Users size={28} />
            </div>
            <div>
              <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Mon Équipe</div>
              <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '400' }}>Gestion Intérimaires</div>
            </div>
          </button>
        )}
      </div>

      <div style={{ marginTop: '60px', textAlign: 'center' }}>
        <button 
          onClick={logout} 
          style={{ 
            padding: '12px 24px', background: 'transparent', border: '1px solid #cbd5e1', color: '#64748b', 
            borderRadius: '50px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}
        >
          <LogOut size={16} /> Déconnexion
        </button>
      </div>
    </div>
  );
}

// --- ROUTAGE PRINCIPAL ---
export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
    >
    <ToastProvider>
    <ConfirmProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* LOGIN */}
          <Route path="/login" element={<Login />} />
          
          {/* DASHBOARD */}
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          
          {/* --- MODULES OPÉRATIONNELS --- */}
          <Route path="/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
          
          {/* MODIFICATION : Validation ouverte aux rôles techniques */}
          <Route 
            path="/timesheet/:employeeId" 
            element={<ProtectedRoute allowedRoles={TECHNICAL_ROLES}><Timesheet /></ProtectedRoute>} 
          />
          
          <Route path="/planning" element={<ProtectedRoute><Planning /></ProtectedRoute>} />
          
          {/* MODIFICATION : Gestion d'équipe ouverte aux rôles techniques */}
          <Route 
            path="/team" 
            element={<ProtectedRoute allowedRoles={TECHNICAL_ROLES}><TeamManager /></ProtectedRoute>} 
          />

          <Route 
            path="/team/profile/:id" 
            element={
              <ProtectedRoute allowedRoles={TECHNICAL_ROLES}>
                <TeamMemberProfile />
              </ProtectedRoute>
            } 
          />
          
          <Route path="/leaves" element={<ProtectedRoute><LeaveRequest /></ProtectedRoute>} />

          {/* --- NOUVELLE ROUTE DOCUMENTS --- */}
          <Route path="/documents" element={<ProtectedRoute><MyDocuments /></ProtectedRoute>} />

          <Route path="/materials" element={<ProtectedRoute><MaterialRequest /></ProtectedRoute>} />
          <Route path="/admin/materials" element={<ProtectedRoute allowedRoles={MANAGEMENT_ROLES}><AdminMaterialsRouter /></ProtectedRoute>} />

          {/* --- ROUTES DOCUMENTATION --- */}
          <Route path="/help/admin" element={<ProtectedRoute allowedRoles={['admin']}><DocAdmin /></ProtectedRoute>} />
          <Route path="/help/employee" element={<ProtectedRoute><DocEmployee /></ProtectedRoute>} />

          {/* --- MODULES SECRETARIAT --- */}
          <Route path="/secretariat" element={
            <ProtectedRoute allowedRoles={['secretariat']}>
              <SecretariatDashboard />
            </ProtectedRoute>
          } />

          <Route path="/secretariat/validate" element={
            <ProtectedRoute allowedRoles={['secretariat']}>
              <SecretariatValidate />
            </ProtectedRoute>
          } />

          <Route path="/secretariat/payroll" element={
            <ProtectedRoute allowedRoles={['secretariat']}>
              <SecretariatPayroll />
            </ProtectedRoute>
            } />

            <Route
            path="/secretariat/documents"
            element={
              <ProtectedRoute allowedRoles={['secretariat']}>
              <SecretariatDocuments />
              </ProtectedRoute>
            }
            />

          <Route path="/secretariat/leaves" element={
            <ProtectedRoute allowedRoles={['secretariat']}>
              <SecretariatLeaves />
            </ProtectedRoute>
          } />

          <Route path="/secretariat/hours" element={
            <ProtectedRoute allowedRoles={['secretariat', 'admin']}>
              <SecretariatHours />
            </ProtectedRoute>
          } />

          {/* --- MODULES ADMINISTRATION --- */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminValidate />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/leaves" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLeaves />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/employees" 
            element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminEmployees />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/projects" 
            element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminProjects />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/reporting" 
            element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminReporting />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planning" 
            element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminPlanning />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planning-edit" 
            element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminScheduler />
              </ProtectedRoute>
            }
            />
            <Route
            path="/admin/documents"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
              <AdminDocuments />
              </ProtectedRoute>
            }
            />
            <Route 
            path="/admin/assistant" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
              <AdminAssistant />
              </ProtectedRoute>
            }
            />

            <Route 
            path="/admin/training" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
              <AdminTraining />
              </ProtectedRoute>
            }
            />
            <Route
              path="/admin/divers"
              element={
              <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                <AdminDivers />
              </ProtectedRoute>
              }
            />

            {/* --- MODULE 5 : ACHATS & COMPARATEUR --- */}
            <Route
              path="/admin/purchases"
              element={
                <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                  <AdminPurchases />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/price-comparator"
              element={
                <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                  <PriceComparator />
                </ProtectedRoute>
              }
            />

            {/* --- MODULE 6 : FACTURATION --- */}
            <Route
              path="/admin/invoicing"
              element={
                <ProtectedRoute allowedRoles={MANAGEMENT_ROLES}>
                  <AdminInvoicing />
                </ProtectedRoute>
              }
            />

            {/* VUE PLANNING 2 SEMAINES (fenêtre dédiée) */}
            <Route
              path="/admin/planning-view"
              element={<PlanningView />}
            />

          {/* REDIRECTION */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ConfirmProvider>
    </ToastProvider>
    </Sentry.ErrorBoundary>
  );
}